import React from 'react'
import { Button } from 'shards-react'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import addFont from '../../../../fonts/fonts'
import styles from '../styles.module.css'
import { METRICS_TOP, METRICS_MONITORING, MONITORING_SECTION_TITLE, fmt } from './metrics'
import { MONITORING_COLUMNS, groupMonitoringByOrg } from './Tables/MonitoringTable'


const fileTitle = 'Отчет об успеваемости (форма №2СВУ)'


export default function Buttons ({ visible, data }) {

  const buildSummaryBody = (row) => {
    const top = METRICS_TOP.map(m => [m.label, fmt(row[m.key])])
    const monitoring = METRICS_MONITORING.map(m => [m.label, fmt(row[m.key])])
    return [
      ...top,
      [{
        content: MONITORING_SECTION_TITLE,
        colSpan: 2,
        styles: { halign: 'center', fontStyle: 'bold', fillColor: [240, 244, 255], textColor: [0, 112, 192] },
      }],
      ...monitoring,
    ]
  }

  const drawPageHeader = (doc, { tableNumber, tableTitle, organizationName, yearTitle }) => {
    const { width } = doc.internal.pageSize
    const margin = 14
    const sideLabelReserve = 70
    let y = 12

    doc.setFontSize(9)
    doc.setFont('Montserrat', 'bold')
    doc.text('Министерство обороны Российской Федерации', width / 2, y, { align: 'center' })
    y += 4
    doc.text('Департамент военного образования', width / 2, y, { align: 'center' })
    doc.setFont('Montserrat', 'normal')
    y += 6

    doc.setFontSize(10)
    doc.text('Форма №2/СВУ', width - margin, y, { align: 'right' })

    doc.setFontSize(11)
    const titleLines = doc.splitTextToSize(
      `${tableTitle} ${yearTitle}`,
      width - margin * 2 - sideLabelReserve,
    )
    titleLines.forEach(line => {
      doc.text(line, width / 2, y, { align: 'center' })
      y += 6
    })

    doc.setFontSize(10)
    doc.text(`Таблица №${tableNumber}`, width - margin, y, { align: 'right' })

    if (organizationName) {
      doc.setFontSize(11)
      const orgLines = doc.splitTextToSize(organizationName, width - margin * 2 - sideLabelReserve)
      orgLines.forEach(line => {
        doc.text(line, width / 2, y, { align: 'center' })
        y += 6
      })
    }

    return y + 2
  }

  const drawSummaryTable = (doc, row, startY) => {
    doc.autoTable({
      startY,
      body: buildSummaryBody(row),
      margin: { left: 14, right: 14 },
      styles: { font: 'Montserrat', fontSize: 9, cellPadding: 2, lineWidth: 0.1, lineColor: 200 },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right', cellWidth: 30 },
      },
    })
  }

  // jsPDF переносит длинные слова по одному символу — split идёт только по
  // пробелу, переносов по слогам не знает. Для слов шире своей колонки
  // расставляем перенос вручную (по правилам русского языка).
  const PDF_HEADER_OVERRIDES = {
    absentCount: 'Отсутство-\nвало на диагностике',
    satisfactoryCount: 'Получили оценку удовлетво-\nрительно',
    unsatisfactoryCount: 'Получили оценку неудовлетво-\nрительно',
  }

  const drawMonitoringTable = (doc, events, startY) => {
    if (!events.length) return

    const head = [[
      '№ п/п',
      ...MONITORING_COLUMNS.map(c => PDF_HEADER_OVERRIDES[c.key] || c.label),
    ]]
    const body = events.map((e, idx) => [
      idx + 1,
      ...MONITORING_COLUMNS.map(c => fmt(e[c.key])),
    ])

    doc.autoTable({
      startY,
      head,
      body,
      margin: { left: 14, right: 14 },
      styles: { font: 'Montserrat', fontSize: 7, cellPadding: 1.5, lineWidth: 0.1, lineColor: 200, halign: 'center', valign: 'middle', overflow: 'linebreak' },
      headStyles: { fillColor: [250, 250, 250], textColor: 20, fontStyle: 'bold', fontSize: 6, halign: 'center', valign: 'middle' },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 22 },
        2: { cellWidth: 30, halign: 'left' },
        3: { cellWidth: 22 },
        4: { cellWidth: 18 },
        5: { cellWidth: 16 },
        6: { cellWidth: 16 },
        7: { cellWidth: 24 },
        8: { cellWidth: 24 },
      },
      theme: 'grid',
    })
  }

  const getPdfData = async () => {
    addFont(jsPDF.API)
    const doc = new jsPDF({ putOnlyUsedFonts: true, orientation: 'p' })

    doc.setFont('Montserrat')

    const summaryRows = data?.summary?.rows || []
    const eventsByOrg = groupMonitoringByOrg(data?.monitoring?.rows)

    const summaryAcademicYear = data?.summary?.academicYear
    const monitoringAcademicYear = data?.monitoring?.academicYear

    const summaryYearTitle = summaryAcademicYear
      ? `в ${summaryAcademicYear} учебном году`
      : 'за учебный год'
    const monitoringYearTitle = monitoringAcademicYear
      ? `в ${monitoringAcademicYear} учебном году`
      : 'за учебный год'

    let isFirstPage = true

    const openPage = (orientation) => {
      if (!isFirstPage) doc.addPage('a4', orientation)
      isFirstPage = false
    }

    summaryRows.forEach(row => {
      const orgId = row.organizationId
      const events = eventsByOrg[orgId] || []

      openPage('p')
      let y = drawPageHeader(doc, {
        tableNumber: 1,
        tableTitle: 'Сведения об успеваемости и качестве учебы',
        organizationName: row.organizationName,
        yearTitle: summaryYearTitle,
      })
      drawSummaryTable(doc, row, y)

      if (events.length) {
        openPage('p')
        y = drawPageHeader(doc, {
          tableNumber: 2,
          tableTitle: 'Сведения о результатах мониторинга (независимой предметной и метапредметной диагностики)',
          organizationName: row.organizationName,
          yearTitle: monitoringYearTitle,
        })
        drawMonitoringTable(doc, events, y)
      }
    })

    return doc
  }

  const toPdf = () => {
    getPdfData().then(doc => doc.save(`${fileTitle}.pdf`))
  }

  if (!visible) return null

  return (
    <div className={styles.buttonsBlock}>
      <Button theme='danger' className={'mb-2'} onClick={toPdf}>
        Скачать .pdf
      </Button>
    </div>
  )
}
